import * as React from "react"
import styled, { PolymorphicComponentProps } from "styled-components"
import { Typography } from "../../typography"

const MenuListEntryContainer = styled(Typography)`
  display: flex;
  list-style: none;
  flex-direction: row;
  align-items: center;
  margin-top: 0.25rem;
  height: 2rem;
`

const MenuButtonEntryContainer = styled(MenuListEntryContainer)`
  margin-top: 0;
  border: none;
  background: transparent;
`

const MenuButton = styled.a<{
  mode: "light" | "dark"
}>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 0.5rem;
  cursor: pointer;
  user-select: none;
  text-decoration: none;

  background: ${({ theme, mode }) => (mode === "light" ? theme.colors.black.$100 : theme.colors.black.$1000)};
  color: ${({ theme, mode }) => (mode === "light" ? theme.colors.grey.$500 : theme.colors.grey.$400)};
`

const MenuItemButton = styled(MenuButton)<{ color?: "danger" }>`
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme, color }) => (color === "danger" ? theme.colors.danger.$200 : theme.colors.black.$1000)};
  padding: 0;
`

const MenuItemGradientButton = styled(MenuButton)`
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.grey.$400};
  }

  &[aria-current="page"] {
    position: relative;
    box-shadow: ${({ theme }) => theme.shadows.midHover};
    color: ${({ theme }) => theme.colors.white};

    &:before {
      position: absolute;
      content: "";
      inset: 0;
      border-radius: 0.5rem;
      border: 1px solid transparent;
      background: linear-gradient(
        43.38deg,
        #e8e3e3 -32.23%,
        #9f9f9f -17.92%,
        #b3f9a7 -8.53%,
        #c3c2ff 14.54%,
        #e4e4e4 23.95%,
        #b9b6b8 35.41%,
        #f3ffdd 52.38%,
        #e5e5e5 67.59%,
        #dfcbff 94.14%,
        #b5a3dc 97.81%
      );
      mask:
        linear-gradient(#fff 0 0) padding-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
    }
  }
`

const MenuItemOffset = styled.span`
  margin-left: 2rem;
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const ChildrenCentered = styled.span`
  padding: 0 1rem;
`

export type DeprecatedMenuListEntryProps<
  C extends React.ElementType,
  D extends React.ElementType,
> = PolymorphicComponentProps<
  "web",
  {
    icon?: React.ReactNode
    centered?: boolean
    mode: "light" | "dark"
    containerProps?: PolymorphicComponentProps<
      "web",
      {
        ["data-testid"]?: string
      },
      D,
      D
    >
  },
  C,
  C
>

export const DeprecatedMenuListEntry = <C extends React.ElementType, D extends React.ElementType>({
  containerProps,
  children,
  centered,
  icon,
  mode,
  ...linkProps
}: DeprecatedMenuListEntryProps<C, D>) => {
  const MenuItem = mode === "light" ? MenuItemButton : MenuItemGradientButton

  return (
    <MenuListEntryContainer as="li" $variant="body-4-regular" {...containerProps}>
      <MenuItem mode={mode} {...linkProps}>
        {centered ? (
          <ChildrenCentered>{children}</ChildrenCentered>
        ) : (
          <MenuItemOffset>
            {icon}

            {children}
          </MenuItemOffset>
        )}
      </MenuItem>
    </MenuListEntryContainer>
  )
}

export const DeprecatedMenuButtonEntry = <C extends React.ElementType = "button", D extends React.ElementType = "li">({
  containerProps,
  children,
  mode,
  ...linkProps
}: DeprecatedMenuListEntryProps<C, D>) => (
  <MenuButtonEntryContainer as="button" $variant="body-4-regular" mode={mode} {...containerProps}>
    <MenuItemButton mode={mode} {...linkProps}>
      <MenuItemOffset>{children}</MenuItemOffset>
    </MenuItemButton>
  </MenuButtonEntryContainer>
)
